import { useApolloClient } from '@apollo/client';
import type { EmbeddingQuery, EmbeddingQueryVariables } from './Embedding.graphql.generated';
import { EmbeddingDocument } from './Embedding.graphql.generated';
import { useEffect, useState } from 'react';
import type { Embedding } from './search.types';

/**
 * Fetches a text embedding vector for `term` via the Embedding GraphQL query,
 * using "latest wins" semantics: when `term` changes rapidly (e.g. from a
 * debounced search input), only the result matching the most recent `term` is
 * kept — earlier in-flight requests are discarded via a `didCancel` flag.
 *
 * Uses `apolloClient.query()` inside an effect rather than `useQuery` so that
 * each term change triggers a fresh request while stale responses are safely
 * ignored. Results are cached via Apollo's `cache-first` policy, so repeated
 * terms resolve instantly.
 *
 * @returns The embedding vector for the current term, or `undefined` while
 *          loading / when `term` is empty.
 */
export function useLatestEmbedding(term: string): Embedding | undefined {
  const apolloClient = useApolloClient();
  const [embedding, setEmbedding] = useState<number[]>();

  useEffect(() => {
    let didCancel = false;

    void (async () => {
      if (!term) {
        setEmbedding(undefined);
        return;
      }
      const result = await apolloClient.query<EmbeddingQuery, EmbeddingQueryVariables>({
        query: EmbeddingDocument,
        variables: { text: term },
        fetchPolicy: 'cache-first',
      });
      if (!didCancel) setEmbedding(result.data?.embedding);
    })();

    return () => {
      didCancel = true;
    };
  }, [apolloClient, term]);

  if (!term) return undefined;
  return embedding;
}
